import { createAction } from '@reduxjs/toolkit';
import type { ActionCreatorWithPayload } from '@reduxjs/toolkit';
import { prefixedActionType } from './redux-websocket/actions';
import {
  WEBSOCKET_BEGIN_RECONNECT,
  WEBSOCKET_BROKEN,
  WEBSOCKET_CLOSED,
  WEBSOCKET_CONNECT,
  WEBSOCKET_DISCONNECT,
  WEBSOCKET_ERROR,
  WEBSOCKET_MESSAGE,
  WEBSOCKET_OPEN,
  WEBSOCKET_RECONNECTED,
  WEBSOCKET_RECONNECT_ATTEMPT,
  WEBSOCKET_SEND,
} from './redux-websocket/actionTypes';
import type { WsError, WsMessagePayload } from './types';

export interface WsActionsMap {
  actionConnect: ActionCreatorWithPayload<Record<string, unknown>>;
  actionOnOpen: ActionCreatorWithPayload<Record<string, unknown>>;
  actionDisconnect: ActionCreatorWithPayload<Record<string, unknown>>;
  actionOnClose: ActionCreatorWithPayload<Record<string, unknown>>;
  actionOnClosed: ActionCreatorWithPayload<Record<string, unknown>>;
  actionSend: ActionCreatorWithPayload<Record<string, unknown>>;
  actionOnMessage: ActionCreatorWithPayload<WsMessagePayload>;
  actionOnBroken: ActionCreatorWithPayload<Record<string, unknown>>;
  actionOnReconnectBegin: ActionCreatorWithPayload<Record<string, unknown>>;
  actionOnReconnectAttempt: ActionCreatorWithPayload<Record<string, unknown>>;
  actionOnReconnected: ActionCreatorWithPayload<Record<string, unknown>>;
  actionOnError: ActionCreatorWithPayload<WsError>;
}

/**
 * Creates all 12 typed action creators for a WS channel, keyed off a prefix string. The types
 * come from `prefixedActionType`, the same helper the redux-websocket action creators use, so the
 * reducers match exactly what the middleware dispatches.
 *
 * @example
 * const mmActions = createWsActions('MM__WS');
 */
export const createWsActions = (prefix: string): WsActionsMap => ({
  actionConnect: createAction<Record<string, unknown>>(
    prefixedActionType(prefix, WEBSOCKET_CONNECT),
  ),
  actionOnOpen: createAction<Record<string, unknown>>(prefixedActionType(prefix, WEBSOCKET_OPEN)),
  actionDisconnect: createAction<Record<string, unknown>>(
    prefixedActionType(prefix, WEBSOCKET_DISCONNECT),
  ),
  actionOnClose: createAction<Record<string, unknown>>(prefixedActionType(prefix, 'CLOSE')),
  actionOnClosed: createAction<Record<string, unknown>>(
    prefixedActionType(prefix, WEBSOCKET_CLOSED),
  ),
  actionSend: createAction<Record<string, unknown>>(prefixedActionType(prefix, WEBSOCKET_SEND)),
  actionOnMessage: createAction<{ message: string } & Record<string, unknown>>(
    prefixedActionType(prefix, WEBSOCKET_MESSAGE),
  ),
  actionOnBroken: createAction<Record<string, unknown>>(
    prefixedActionType(prefix, WEBSOCKET_BROKEN),
  ),
  actionOnReconnectBegin: createAction<Record<string, unknown>>(
    prefixedActionType(prefix, WEBSOCKET_BEGIN_RECONNECT),
  ),
  actionOnReconnectAttempt: createAction<Record<string, unknown>>(
    prefixedActionType(prefix, WEBSOCKET_RECONNECT_ATTEMPT),
  ),
  actionOnReconnected: createAction<Record<string, unknown>>(
    prefixedActionType(prefix, WEBSOCKET_RECONNECTED),
  ),
  actionOnError: createAction<WsError>(prefixedActionType(prefix, WEBSOCKET_ERROR)),
});
